import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import get from 'lodash/get'
import { ICON_NAMES } from '../../../../../constants'
import {
  AGGREGATION_LEVEL_VALUES,
  DEFAULT_AGGREGATION_LEVELS,
  PLAN_DATE_FORMAT_KEY
} from './constants'

dayjs.extend(utc)

export const URGENT_FILTER_KEY = 'urgent'

const LEVEL_CATEGORIES = 'categories'
const LEVEL_ITEMS = 'items'

const FALLBACK_STATUS_STYLE = {
  color: 'var(--text-color-subtle)',
  icon: ICON_NAMES.radioUnchecked,
  order: 0
}

const BUILT_IN_STATUS_STYLES = {
  'On Track': { color: 'var(--color-growth-500)', icon: ICON_NAMES.checkCircle, order: 30 },
  'In Progress': { color: 'var(--color-warning-500)', icon: ICON_NAMES.clock, order: 20 },
  'Not Started': { color: 'var(--color-neutral-400)', icon: ICON_NAMES.radioUnchecked, order: 10 },
  Complete: { color: 'var(--color-growth-500)', icon: ICON_NAMES.checkCircle, order: 40 },
  'Not Now': { color: 'var(--color-neutral-400)', icon: ICON_NAMES.block, order: 5 }
}

// No `order` here — the urgent pill is appended after the rest of the pills
// have been sorted, so it always sits at the end of the row.
export const URGENT_PILL_STYLE = {
  color: 'var(--color-decline-500)',
  icon: ICON_NAMES.warning
}

export function getStatusStyle (statusString, overrides) {
  if (!statusString) return FALLBACK_STATUS_STYLE
  return overrides?.[statusString] ?? BUILT_IN_STATUS_STYLES[statusString] ?? FALLBACK_STATUS_STYLE
}

// Normalize an `aggregationLevels` config value into a `Set` of the active
// levels. Falls back to the default (both levels) when the value is
// missing, empty, or only contains unknown values — keeps the UI useful
// rather than rendering a zero-pill row.
export function resolveActiveLevels (aggregationLevels) {
  const raw = Array.isArray(aggregationLevels) ? aggregationLevels : null
  if (!raw) return new Set(DEFAULT_AGGREGATION_LEVELS)
  const filtered = raw.filter(level => AGGREGATION_LEVEL_VALUES.includes(level))
  if (filtered.length === 0) return new Set(DEFAULT_AGGREGATION_LEVELS)
  return new Set(filtered)
}

// Walks the nodes of a plan honoring the active levels. The plan itself is
// always excluded from filter pills/counts since it is rendered as the
// container ("The Plan") rather than a filterable row.
export function walkNodes (plan, levels, visit) {
  if (!plan) return
  const includeCategories = levels.has(LEVEL_CATEGORIES)
  const includeItems = levels.has(LEVEL_ITEMS)
  for (const cat of plan.data ?? []) {
    if (includeCategories) visit(cat)
    if (includeItems) {
      for (const item of cat.data ?? []) {
        visit(item)
      }
    }
  }
}

export function deriveFilterPills (plan, statusOverrides, levels) {
  if (!plan) return []
  const counts = new Map()
  let urgentCount = 0
  walkNodes(plan, levels, (node) => {
    if (node?.status) counts.set(node.status, (counts.get(node.status) ?? 0) + 1)
    if (node?.metadata?.needsAttention) urgentCount += 1
  })
  const pills = Array.from(counts.entries()).map(([status, count]) => ({
    key: status,
    label: status,
    count,
    style: getStatusStyle(status, statusOverrides)
  }))
  pills.sort((a, b) => (b.style.order ?? 0) - (a.style.order ?? 0))
  if (urgentCount > 0) {
    pills.push({
      key: URGENT_FILTER_KEY,
      label: 'Urgent',
      count: urgentCount,
      style: URGENT_PILL_STYLE
    })
  }
  return pills
}

export function countTotalItems (plan, levels) {
  if (!plan) return 0
  let total = 0
  walkNodes(plan, levels, () => { total += 1 })
  return total
}

function nodeMatchesFilter (node, currentFilter) {
  if (!currentFilter) return false
  if (currentFilter === URGENT_FILTER_KEY) return !!node?.metadata?.needsAttention
  return node?.status === currentFilter
}

// The filter retention rule depends on which levels are aggregated:
//   - both (default): a matching category retains all of its items; otherwise
//     only matching items are kept (legacy behavior).
//   - items only: category is kept iff it has at least one matching item;
//     only matching items remain.
//   - categories only: category is kept iff its own status matches; all of
//     its items remain.
export function applyStatusFilter (plan, currentFilter, levels) {
  if (!plan || !currentFilter) return plan
  const includeCategories = levels.has(LEVEL_CATEGORIES)
  const includeItems = levels.has(LEVEL_ITEMS)

  const filteredCategories = (plan.data ?? [])
    .map(cat => {
      const categoryMatches = includeCategories && nodeMatchesFilter(cat, currentFilter)
      if (categoryMatches) return cat
      if (!includeItems) return null
      const matchingItems = (cat.data ?? []).filter(item => nodeMatchesFilter(item, currentFilter))
      if (matchingItems.length === 0) return null
      return { ...cat, data: matchingItems }
    })
    .filter(Boolean)

  return { ...plan, data: filteredCategories }
}

// True when the active filter has at least one matching item under the
// category, regardless of the category's own status. Drives auto-expand.
// Returns false when items are not part of the aggregation set since no
// item-level match is possible.
export function categoryHasMatchingItem (category, currentFilter, levels) {
  if (!currentFilter || !category || !levels.has(LEVEL_ITEMS)) return false
  return (category.data ?? []).some(item => nodeMatchesFilter(item, currentFilter))
}

// Locate any node by id. Returns `{ kind: 'category' | 'item', node, parent }`
// or null. Used by the side panel to render either a selected category or item.
export function findNode (plan, nodeId) {
  if (!plan || !nodeId) return null
  for (const cat of plan.data ?? []) {
    if (cat.id === nodeId) {
      return { kind: 'category', node: cat, parent: plan }
    }
    for (const item of cat.data ?? []) {
      if (item.id === nodeId) {
        return { kind: 'item', node: item, parent: cat }
      }
    }
  }
  return null
}

export function formatPlanDate (iso) {
  if (!iso) return null
  const d = dayjs.utc(iso)
  if (!d.isValid()) return null
  return d.format('MM/DD/YY')
}

// Detects whether a description string should be rendered as HTML or Markdown.
// Salesforce-sourced descriptions can come back as raw HTML (`<p>...</p>`) or
// as HTML-entity-encoded HTML (`&lt;p&gt;...&lt;/p&gt;`). Either form is
// treated as HTML so Markup can sanitize/unescape it via dompurify; anything
// else falls through to Markdown so legacy `**bold**` etc still works.
export function detectMarkupType (value) {
  if (!value || typeof value !== 'string') return 'plain'
  if (/<[a-z][\s\S]*>/i.test(value)) return 'html'
  if (/&lt;[a-z]/i.test(value)) return 'html'
  return 'markdown'
}

// Treats null, undefined, empty strings, and empty arrays as "empty" for
// the purposes of `hideWhenEmpty` row suppression. Numbers (including 0)
// and booleans (including false) are NOT empty — admins can decide to
// display them.
export function isValueEmpty (value) {
  if (value === null || value === undefined) return true
  if (typeof value === 'string' && value.trim() === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

// Reads a metadata row value off of a node using a dot path accessor and
// formats it via the shared `FormattingContext` formatter. The `planDate`
// format key is a legacy short-circuit that preserves the historical
// MM/DD/YY rendering used by `formatPlanDate` (the shared `date` alias
// renders M/D/YYYY).
export function resolveMetadataValue (node, row, formatter) {
  if (!node || !row) return null
  const accessor = typeof row.accessor === 'string' ? row.accessor.trim() : ''
  if (!accessor) return null
  const raw = get(node, accessor)
  if (isValueEmpty(raw)) return null
  if (row.format === PLAN_DATE_FORMAT_KEY) return formatPlanDate(raw)
  if (typeof formatter === 'function') return formatter(raw, row.format ?? undefined)
  return raw
}

// Returns the task ids attached to a node (plan / category / item). Prefers
// the explicit `taskIds` array; falls back to extracting ids from a `tasks`
// array if present. Returns null when the node has no linkage at all.
export function resolveItemTaskIds (item) {
  if (!item) return null
  if (Array.isArray(item.taskIds)) {
    return item.taskIds.filter(id => id != null).map(String)
  }
  if (Array.isArray(item.tasks)) {
    return item.tasks
      .map(t => t?.id)
      .filter(id => id != null)
      .map(String)
  }
  return null
}
